import Cookies from "js-cookie";
import { isNumber, size } from "lodash";
import { hideLoading } from "react-redux-loading-bar";
import { AnyAction } from "redux";
import { createSelector } from "reselect";
import { createAssign } from "robodux";
import { call, delay, fetchRetry, put, select, spawn } from "saga-query";

import { service } from "@app/service";
//--
import { api, thunks } from "@app/state/apis/api";
import { pollMatrix } from "@app/state/middlewares/pollMatrix";
import { initPxWorker } from "@app/statefx/workers";
import {
  getPxWorker,
  startPxWorker,
  terminatePxWorker,
} from "@app/statefx/workers/pxworker-factory";

import { PERSISTOR_NAME } from "../constants";
import { insLogout } from "../insider/login";
import { resetStafxPersistor, resetStore } from "../resetStore";
import { adminsClear, adminsInitialize } from "./admins";
import { getSvnFileSys, loadFileSys } from "./fileSys";
import { setGroupData } from "./groups";
import { appNotify } from "./notify";
import { selectLastLoginEmail, setLastLoginEmail } from "./persistorGuard";
import { showcaseInitialize } from "./showCase";

// import { checkSession } from './utils';

import type { ApiCtx } from "saga-query";
import type { RootState } from "../rootState";
import type { IAuth0User, IWhoAmI, IDBFusionUser, TRoles } from "@app/types";
//NOTE that @SWCRE is a special case, we don't rank it
const roleRank = {
  REGUEST: 1,
  JGUSER: 2,
  JGADMIN: 3,
  SWS: 4,
} as Record<TRoles, number>;

export function getMaxRole(roles: TRoles[] = []) {
  const maxRole = roles.reduce((acc: TRoles, role: TRoles) => {
    if (roleRank[role] > roleRank[acc]) {
      return role;
    }
    return acc;
  }, "REGUEST");
  return maxRole;
}

export function getMinRole(roles: TRoles[] = []) {
  const minRole = roles.reduce((acc: TRoles, role: TRoles) => {
    if (roleRank[role] < roleRank[acc]) {
      return role;
    }
    return acc;
  }, "SWS");
  return minRole;
}

function dec2hex(dec: number) {
  return ("0" + dec.toString(16)).substr(-2);
}
function generateRandomString() {
  const array = new Uint32Array(56 / 2);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec2hex).join("");
}

const REPO_NAME = "login";

const noFuUser = {
  email: "",
  email_verified: false,
  roles: [],
  ALGID: null,
  PERSEED: "",
  IS_ACTIVE: false,
  FIRST_NAME: "",
  LAST_NAME: "",
  ACCESS_LEVEL: 0,
  groupids: [],
  token: "",
  pwdless: false,
};

export const loginRepo = createAssign<IWhoAmI>({
  name: REPO_NAME,
  initialState: noFuUser,
  extraReducers: {
    login_test_action: (state: RootState, action: AnyAction) => {
      return { ...state, teststuff: action.payload };
    },
  },
});

export const testLoginStatus = api.get(
  "/login/test",
  function* (ctx: ApiCtx<any, IWhoAmI>, next) {
    ctx.request = ctx.req({
      url: `/fu-whoami/dry`,
      credentials: "include",
    });
    yield* next();
    const { data } = yield ctx.json;
    console.log("data", data);
    const ldata = data?.[0] || {};
  }
);

export const setAuthResult = api.get<IAuth0User>(
  "/login/auth0",
  function* (ctx: ApiCtx<any, IAuth0User>, next) {
    // if auth0 returns false but the user is in then we leave the user in
    if (ctx.payload.email !== "") {
      loginRepo.actions.set(ctx.payload);
      localStorage.setItem("login_hint", ctx.payload.email);
    }
    ctx.request = ctx.req({
      url: `/login/auth0?${new URLSearchParams(ctx.payload)}`,
    });
    yield* next();
    const { data } = yield ctx.json as { data: IWhoAmI[] };
    const ldata = data?.[0] || {};
    const logindata = {
      UID: ldata.UID,
      RIGHTS: ldata.RIGHTS,
      FIRST_NAME: ldata.FIRST_NAME,
      LAST_NAME: ldata.LAST_NAME,
      DISPLAY_NAME: ldata.DISPLAY_NAME,
      HOME_BRANCH: ldata.HOME_BRANCH,
      CONTACTS1: ldata.CONTACTS1,
      CONTACTS2: ldata.CONTACTS2,
      CONTACTS3: ldata.CONTACTS3,
      NOTES: ldata.NOTES,
    };
    yield put(loginRepo.actions.set({ ...ctx.payload, ...logindata }));
    // start initial fetching  ....
    if (isNumber(ldata.UID) && ldata.UID > 0) {
      // yield* call(runInitialLoad.run, runInitialLoad({ UID: ldata.UID }));
      // yield* put(uiiApii({ currentPage: "workdata/any" }));
    } else {
      yield spawn(
        appNotify.run,
        appNotify({
          type: "error",
          message: "Utiliztorul nu are un identificator valid",
          duration: 5000,
        })
      );
    }
  }
);

export const logout = api.get(
  "/login/logout",
  function* (ctx: ApiCtx<any, unknown, any>, next): any {
    yield* next();
    ctx.json;
    // yield* put(pollService(pollEndpoint()).stop());
    yield* put(loginRepo.actions.set(noFuUser));
    yield* put({ type: "logout/RESET_STORE" });
    yield* delay(700);
    yield* put(hideLoading());
  }
);

export const nop = api.get(
  "/nop",
  function* (ctx: ApiCtx<any, unknown, any>, next): any {
    yield* next();
  }
);

// the login function is inspired from the FusionAuth React SDK
// https://github.com/FusionAuth/fusionauth-react-sdk/src/providers/FusionAuthProvider.tsx

export const fusionLogin = thunks.create<any>(
  "/login-form",
  function* (ctx: ApiCtx<any, unknown, void>, next) {
    const state = ctx.payload?.state || "";
    const stateParam = `${generateRandomString()}:${state}`;
    Cookies.set("lastState", stateParam); // save state for later (todo:check if needed)

    const fusionParams = {
      client_id: process.env.REACT_APP_CLIENTID || "",
      scope: "offline_access openid",
      redirectUri:
        process.env.NODE_ENV === "production"
          ? process.env.REACT_APP_REDIRECTURI_PROD || ""
          : process.env.REACT_APP_REDIRECTURI_DEV || "",
      state: stateParam,
      locale: "ro_RO",
    };
    const serverUrl =
      process.env.NODE_ENV === "production"
        ? process.env.REACT_APP_SERVERURL_PROD || ""
        : process.env.REACT_APP_SERVERURL_DEV || "";
    const queryParams = new URLSearchParams(fusionParams);
    const fullUrl = `${serverUrl}${service}/fu-login?${queryParams}`;
    yield* next();
    window.location.assign(fullUrl);
  }
);

export const fusionLogout = thunks.create(
  "/logout-form",
  function* (ctx, next): any {
    Cookies.remove("lastState");
    const queryParams = {
      client_id: process.env.REACT_APP_CLIENTID || "",
      post_logout_redirect_uri:
        process.env.NODE_ENV === "production"
          ? process.env.REACT_APP_REDIRECTURI_PROD || ""
          : process.env.REACT_APP_REDIRECTURI_DEV || "",
      locale: "ro_RO",
    };
    const serverUrl =
      process.env.NODE_ENV === "production"
        ? process.env.REACT_APP_SERVERURL_PROD || ""
        : process.env.REACT_APP_SERVERURL_DEV || "";
    const query = new URLSearchParams(queryParams);
    const queryString = query.toString().length > 0 ? `?${query}` : "";
    const fullUrl = `${serverUrl}${service}/fu-logout${queryString}`;
    ctx.actions.push(loginRepo.actions.set(noFuUser));
    ctx.actions.push({ type: "logout/RESET_STORE" });
    ctx.actions.push(hideLoading());
    ctx.actions.push(insLogout());
    ctx.actions.push({ type: "@@saga-query/data/reset" });
    yield* next();
    window.location.assign(fullUrl);
  }
);

export const fusionRegister = thunks.create(
  "/register-form",
  function* (ctx, next): any {
    const state = ctx.payload || "";
    const stateParam = `${generateRandomString()}:${state}`;
    Cookies.set("lastState", stateParam);
    const fusionParams = {
      client_id: process.env.REACT_APP_CLIENTID || "",
      scope: "offline_access openid",
      redirectUri:
        process.env.NODE_ENV === "production"
          ? process.env.REACT_APP_REDIRECTURI_PROD || ""
          : process.env.REACT_APP_REDIRECTURI_DEV || "",
      state: stateParam,
      locale: "ro_RO",
    };
    const serverUrl =
      process.env.NODE_ENV === "production"
        ? process.env.REACT_APP_SERVERURL_PROD || ""
        : process.env.REACT_APP_SERVERURL_DEV || "";
    const queryParams = new URLSearchParams(fusionParams);
    const fullUrl = `${serverUrl}${service}/fu-register?${queryParams}`;
    window.location.assign(fullUrl);
    yield* next();
  }
);

export const emailVerified = api.get("/fu-email-verified", [
  function* (ctx: ApiCtx<any, unknown, any>, next): any {
    ctx.request = ctx.req({
      url: `/fu-whoami/fu-email-verified`,
    });
    ctx.bodyType = "text";
    yield* next();
    const { ok, data } = yield ctx.json;
    if (ok && data === "OK") {
      yield* call(whoami.run, whoami()); // refresh user data
      return;
    }
  },
  fetchRetry((n) => (n < 20 ? 15000 : -1)),
]);

export const fusionWhoami = api.get(
  "/fu-whoami",
  function* (ctx: ApiCtx<any, IDBFusionUser>, next) {
    ctx.request = ctx.req({
      url: `/fu-whoami`,
      credentials: "include",
    });
    yield* next();
    const { ok, data } = yield ctx.json as any;
    const { email, email_verified } = data || {};

    if (email) {
      yield* put(loginRepo.actions.set({ ...noFuUser, ...data }));
    }
    if (email && !email_verified) {
      yield* call(emailVerified.run, emailVerified());
    }
  }
);

export const fusionRefreshSession = api.get(
  "/fu-token-refresh",
  { saga: pollMatrix(15 * 1000) },
  function* (ctx: ApiCtx<any, IDBFusionUser>, next) {
    ctx.request = ctx.req({
      url: `/fu-token-refresh`,
      credentials: "include",
      method: "POST",
    });
    yield* next();
    const { ok } = yield ctx.response;
    if (ok) {
      yield* call(fusionWhoami.run, fusionWhoami());
    } else {
      yield* spawn(
        appNotify.run,
        appNotify({
          type: "error",
          message: "Session expired. Please login again.",
          duration: 5000,
        })
      );
    }
  }
);

/* -------------------------------------------------------------------------- */

export const whoami = api.get(
  "/logins/whoami",
  function* (ctx: ApiCtx<any, IWhoAmI>, next) {
    ctx.cache = false;
    ctx.request = ctx.req({
      url: `/logins/whoami`,
      credentials: "include",
    });
    yield* next();
    const { data } = yield ctx.json;
    const { email, email_verified } = data || {};
    if (email) {
      const { groups, ...loginData } = data;
      const groupids = size(groups) ? Object.keys(groups).map((n) => +n) : [];
      yield* put(
        loginRepo.actions.set({ ...noFuUser, ...loginData, groupids })
      );
      if (email_verified) {
        // full login //
        yield* put(setGroupData(groups));

        yield* put(showcaseInitialize(email));

        // imporant https://github.com/VldMrgnn/jgap/issues/33
        // if persistor is of another user then cleanup //
        const persistedOwner = yield* select(selectLastLoginEmail);
        if (!!persistedOwner && persistedOwner !== email) {
          yield* put(resetStore());
          // yield* put(resetStafxPersistor());
        }
        const pxWorker = getPxWorker();

        if (pxWorker) {
          pxWorker.postMessage({
            type: "/upgrade",
            data: JSON.stringify({ email, perseed: "" }),
          });
        }
        yield* put(setLastLoginEmail(email));

        const maxRole: TRoles = getMaxRole(loginData.roles);
        if (maxRole === "SWS") {
          yield* put(adminsInitialize());
          yield* put(getSvnFileSys());
        } else {
          yield* put(adminsClear());
        }
        yield* put(loadFileSys());
        // yield* put(pollService(pollEndpoint()).start());
      }
    }
    if (email && !email_verified) {
      yield* call(emailVerified.run, emailVerified());
      return;
    }
  }
);

export const updateWhoami = api.post<IWhoAmI>(
  "/logins/whoami",
  function* (ctx: ApiCtx<any, IWhoAmI>, next) {
    ctx.request = ctx.req({
      url: `/logins/whoami`,
      credentials: "include",
      method: "POST",
      body: JSON.stringify(ctx.payload),
    });
    yield* next();
    const { ok, data } = yield ctx.json;
    if (ok) {
      yield* spawn(
        appNotify.run,
        appNotify({
          type: "success",
          message: "Profile updated",
          duration: 2000,
          position: "bottom",
        })
      );
    }
    // on nochange we get 204 and no data
    if (ctx?.response?.status === 200 && ok) {
      const { groups, ...loginData } = data;
      yield* put(loginRepo.actions.set({ ...noFuUser, ...loginData }));
      if (groups) {
        yield* put(setGroupData(groups));
      }
    }
  }
);
/* -------------------------------------------------------------------------- */

export const selectLogin = (state: RootState) => state[REPO_NAME];

export const isAuth0 = createSelector(
  selectLogin,
  (login) => login.email !== "" && login.email_verified
);

export const isAuth0AndAma = createSelector(selectLogin, (login) => {
  if (!login?.email) return false;
  return (
    (login.email || "") !== "" &&
    login.email_verified &&
    Number.isInteger(+login.UID) &&
    login.UID > 0
  );
});

export const whoamiSelect = createSelector(selectLogin, (login) => login);

export const selectUserEmail = createSelector(selectLogin, (login) => {
  return login.email;
});

export const selectIsPasswordeless = createSelector(selectLogin, (login) => {
  return login.pwdless === true;
});
export const selectMaxRole = createSelector(selectLogin, (login) => {
  //TODO: if in passwordless restricted session we hide all the stuff
  // so, we will set the role to REGUEST //
  return getMaxRole(login.roles);
});

export const whoamiAsLonleyUser = createSelector(
  selectLogin,
  (login) => login.PERSEED
);

// const selectors = {
//   isAuth0,
//   isAuth0AndAma,
//   whoamiSelect,
// };
// const actions = {
//   setAuthResult,
//   logout,
//   nop,
// };

// export const agregateObject_login = {
//   actions,
//   selectors,
// };
